import type { Config, DriveStep, Driver } from "driver.js";
import { localText, serviceRequest } from "@serenity-is/corelib";
import { canonicalScreenUrl, screenUrlMatches, searchHelp, type HelpScreenDoc, type TextRange } from "./HelpSearch";
import {
    applyLoadedPreference,
    defaultHelpPreference,
    helpStateStorageKey,
    preferenceBeforeLoad,
    shouldStartTour,
    type HelpPreferenceState
} from "./HelpTraining";
import { stampTourAnchors } from "./TourAnchors";

/**
 * Help button, slide-out panel, guided tours, and client-side search.
 * Screen copy and tour steps come from /Help/Screen. Search reads /Help/Index
 * once (help articles plus the screens this user can open) and matches it in
 * the browser. Sikka's poses: wave (panel), point (steps), think (loading and
 * empty), celebrate (tour finished).
 *
 * Training mode is the only automatic switch. While it is off, the greeting,
 * the panel, the generic grid tour, and the dialog-tour runner stay quiet on
 * the first visit and on every later navigation. The ? button, Ctrl+/, and
 * Start tour (including ?helpTour=1) still work. Turning it on offers the
 * current screen's tour on every visit again.
 */

interface TourStep {
    element?: string | HTMLElement;
    text?: string;
    side?: "top" | "right" | "bottom" | "left" | "over";
    /** Set when the selector was prefixed with @dialog. The runner opens the edit dialog first. */
    openDialog?: boolean;
}

interface ResolvedTour {
    id: string;
    steps: TourStep[];
}

interface ScreenTour {
    id?: string;
    steps?: TourStep[];
}

interface ScreenHelp {
    id?: string;
    title?: string;
    keys?: string[];
    routes?: string[];
    query?: Record<string, string>;
    howtoTitle?: string;
    steps?: string[];
    videoUrl?: string;
    videoCaption?: string;
    helpCenterKey?: string;
    searchText?: string[];
    tour?: ScreenTour;
}

interface HelpIndex {
    articles: ScreenHelp[];
    screens: HelpScreenDoc[];
}

type HelpState = HelpPreferenceState;

const mascotFile = {
    wave: "sikka-wave.svg",
    point: "sikka-point.svg",
    think: "sikka-think.svg",
    celebrate: "sikka-celebrate.svg"
};

let panel: HTMLElement = null;
let backdrop: HTMLElement = null;
let openButton: HTMLButtonElement = null;
let searchInput: HTMLInputElement = null;
let screen: ScreenHelp = null;
let screenReady = false;
let searchQuery = "";
let pinned: ScreenHelp = null;
let indexCache: HelpIndex = null;
let indexPromise: Promise<HelpIndex> = null;
let searchGeneration = 0;
let state: HelpState = defaultHelpPreference();
let preferenceRevision = 0;
let preferencesReady: Promise<void> = null;
type DriverFactory = NonNullable<NonNullable<Window["driver"]>["js"]>["driver"];

let tour: Driver = null;
let autoTourActive = false;
let suppressTourRemember = false;
let offered = false;
let screenToken = 0;
let activeLocation = "";
let cancelGreet: ((remember: boolean) => void) = null;
let driverLoad: Promise<DriverFactory | null> = null;

function text(key: string, fallback: string): string {
    const value = localText(key, fallback);
    return value && value !== key ? value : fallback;
}

function format(template: string, ...args: string[]): string {
    return template.replace(/\{(\d+)\}/g, (_, index) => args[Number(index)] ?? "");
}

function appRoot(): string {
    const link = document.getElementById("ApplicationPath") as HTMLLinkElement;
    let href = link?.getAttribute("href") || "/";
    if (href.startsWith("~/"))
        href = href.slice(1);
    if (!href.endsWith("/"))
        href += "/";
    return href;
}

function pagePath(): string {
    const root = appRoot();
    let path = location.pathname || "/";
    const prefix = root.length > 1 ? root.slice(0, -1) : "";
    if (prefix && path.toLowerCase().startsWith(prefix.toLowerCase()))
        path = path.slice(prefix.length) || "/";
    return path;
}

function asset(file: string): string {
    return appRoot() + "Content/help/mascot/" + file;
}

function driverScriptUrl(): string {
    return appRoot() + "Scripts/driver/driver.js";
}

/** Loads the vendored driver.js. A missing or broken script disables tours only. */
function loadDriver(): Promise<DriverFactory | null> {
    if (!driverLoad) {
        driverLoad = new Promise(resolve => {
            const ready = window.driver?.js?.driver;
            if (typeof ready === "function") {
                resolve(ready);
                return;
            }
            const script = document.createElement("script");
            script.src = driverScriptUrl();
            script.async = true;
            script.onload = () => resolve(typeof window.driver?.js?.driver === "function"
                ? window.driver.js.driver
                : null);
            script.onerror = () => resolve(null);
            document.head.appendChild(script);
        });
    }
    return driverLoad;
}

function helpCenterUrl(): string {
    return appRoot() + "HelpCenter";
}

function userStorageKey(): string {
    const name = document.querySelector("meta[name='username']")?.getAttribute("content") || "";
    return helpStateStorageKey(name);
}

function readLocal(): HelpState {
    try {
        return preferenceBeforeLoad(localStorage.getItem(userStorageKey()));
    }
    catch {
        return defaultHelpPreference();
    }
}

function writeLocal(value: HelpState) {
    try {
        localStorage.setItem(userStorageKey(), JSON.stringify(value));
    }
    catch {
        /* a private window can refuse storage; the server copy still saves */
    }
}

function locationToken(): string {
    const params = new URLSearchParams(location.search);
    params.delete("helpTour");
    params.sort();
    const query = params.toString();
    return location.pathname + (query ? "?" + query : "") + location.hash;
}

function safeMedia(url: string): string {
    if (!url)
        return "";
    if (url.startsWith("/") && !url.startsWith("//"))
        return url;
    try {
        const parsed = new URL(url, location.origin);
        if (parsed.protocol === "http:" || parsed.protocol === "https:")
            return parsed.href;
    }
    catch {
        /* ignore */
    }
    return "";
}

function ensureChrome() {
    if (!openButton) {
        openButton = document.getElementById("s-help-open") as HTMLButtonElement;
        if (!openButton) {
            openButton = document.createElement("button");
            openButton.type = "button";
            openButton.id = "s-help-open";
            openButton.className = "s-help-open s-help-open-floating";
            openButton.textContent = "?";
            document.body.appendChild(openButton);
        }
        paintButton(openButton);
        openButton.addEventListener("click", () => {
            if (panel?.classList.contains("s-open"))
                closePanel();
            else
                void openPanel();
        });
    }

    if (backdrop)
        return;

    backdrop = document.createElement("div");
    backdrop.className = "s-help-backdrop";
    backdrop.addEventListener("click", closePanel);

    panel = document.createElement("aside");
    panel.id = "s-help-panel";
    panel.className = "s-help-panel";
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-modal", "false");
    panel.setAttribute("aria-hidden", "true");
    panel.setAttribute("aria-label", text("Help.PanelTitle", "Help"));
    panel.innerHTML =
        '<div class="s-help-panel-header">' +
            '<img class="s-help-mascot" alt="" />' +
            '<div class="s-help-panel-heading">' +
                '<div class="s-help-panel-kicker"></div>' +
                '<h2 class="s-help-panel-title"></h2>' +
                '<p class="s-help-panel-hi"></p>' +
            '</div>' +
            '<button type="button" class="s-help-panel-close"></button>' +
        '</div>' +
        '<div class="s-help-panel-body" id="s-help-panel-body"></div>';

    const searchWrap = document.createElement("div");
    searchWrap.className = "s-help-search";
    searchWrap.setAttribute("role", "search");
    const searchLabel = document.createElement("label");
    searchLabel.className = "s-help-search-label";
    searchLabel.htmlFor = "s-help-search-input";
    searchLabel.textContent = text("Help.SearchLabel", "Search help");
    searchInput = document.createElement("input");
    searchInput.id = "s-help-search-input";
    searchInput.type = "search";
    searchInput.autocomplete = "off";
    searchInput.spellcheck = false;
    searchInput.placeholder = text("Help.SearchPlaceholder", "Search help and screens");
    searchInput.setAttribute("aria-keyshortcuts", "Control+/");
    searchInput.setAttribute("aria-controls", "s-help-panel-body");
    searchInput.addEventListener("input", () => {
        searchQuery = searchInput.value;
        pinned = null;
        searchGeneration++;
        render();
    });
    searchWrap.append(searchLabel, searchInput);
    const body = panel.querySelector(".s-help-panel-body");
    panel.insertBefore(searchWrap, body);

    const mascot = panel.querySelector(".s-help-mascot") as HTMLImageElement;
    mascot.src = asset(mascotFile.wave);
    mascot.alt = text("Help.MascotAlt", "Sikka");
    panel.querySelector(".s-help-panel-kicker").textContent = text("Help.PanelTitle", "Help");
    panel.querySelector(".s-help-panel-hi").textContent = text("Help.Greeting", "Hi, I'm Sikka");
    const close = panel.querySelector(".s-help-panel-close") as HTMLButtonElement;
    close.textContent = "×";
    close.setAttribute("aria-label", text("Help.Close", "Close"));
    close.addEventListener("click", closePanel);

    document.body.appendChild(backdrop);
    document.body.appendChild(panel);
}

function paintButton(button: HTMLButtonElement) {
    button.setAttribute("aria-label", text("Help.OpenHelp", "Help"));
    button.title = text("Help.OpenHelp", "Help");
    if (button.querySelector("img"))
        return;
    button.textContent = "";
    const img = document.createElement("img");
    img.src = asset(mascotFile.wave);
    img.alt = "";
    const label = document.createElement("span");
    label.className = "s-help-open-label";
    label.textContent = text("Help.OpenHelp", "Help");
    button.append(img, label);
}

function maybeGreet() {
    if (!shouldStartTour("auto", state) || cancelGreet)
        return;
    const key = "mERP26.help.greeted";
    try {
        if (localStorage.getItem(key))
            return;
    }
    catch {
        return;
    }
    if (!openButton)
        return;

    const bubble = document.createElement("div");
    bubble.className = "s-help-greet";
    const img = document.createElement("img");
    img.src = asset(mascotFile.wave);
    img.alt = "";
    const message = document.createElement("p");
    message.textContent = text("Help.Greeting", "Hi, I'm Sikka");
    bubble.append(img, message);
    document.body.appendChild(bubble);

    const place = () => {
        const rect = openButton.getBoundingClientRect();
        const floating = openButton.classList.contains("s-help-open-floating");
        const width = bubble.offsetWidth || 200;
        const height = bubble.offsetHeight || 48;
        const left = Math.min(Math.max(8, rect.left - width - 10), window.innerWidth - width - 8);
        bubble.style.left = left + "px";
        bubble.style.top = (floating
            ? Math.max(8, rect.top - height - 10)
            : rect.bottom + 8) + "px";
    };
    place();

    let gone = false;
    const dismiss = (remember: boolean) => {
        if (gone)
            return;
        gone = true;
        if (cancelGreet === dismiss)
            cancelGreet = null;
        if (remember) {
            try {
                localStorage.setItem(key, "1");
            }
            catch {
                /* ignore */
            }
        }
        bubble.remove();
        window.removeEventListener("resize", place);
    };
    cancelGreet = dismiss;
    window.addEventListener("resize", place);
    bubble.addEventListener("click", () => dismiss(true));
    openButton.addEventListener("click", () => dismiss(true), { once: true });
    window.setTimeout(() => dismiss(true), 8000);
}

function dismissAutoGreet() {
    cancelGreet?.(false);
}

function stopAutoTour() {
    if (!autoTourActive || !tour)
        return;
    const running = tour;
    autoTourActive = false;
    suppressTourRemember = true;
    try {
        running.destroy();
    }
    finally {
        suppressTourRemember = false;
        if (tour === running)
            tour = null;
    }
}

function stopAutoHelp() {
    dismissAutoGreet();
    stopAutoTour();
}

function endRunningTour() {
    tour?.destroy();
}

function showTourUnavailable() {
    ensureChrome();
    if (!panel.classList.contains("s-open"))
        void openPanel();
    const body = panel.querySelector(".s-help-panel-body");
    if (!body || body.querySelector(".s-help-tour-unavailable"))
        return;
    const note = document.createElement("p");
    note.className = "s-help-tour-unavailable";
    note.textContent = text("Help.TourUnavailable",
        "Guided tours aren't available right now. The steps above still apply.");
    body.prepend(note);
}

function setTitle(value: string) {
    const title = panel.querySelector(".s-help-panel-title");
    title.textContent = value || text("Help.PanelTitle", "Help");
}

function renderLoading() {
    const body = panel.querySelector(".s-help-panel-body");
    body.replaceChildren();
    setTitle(text("Help.PanelTitle", "Help"));
    const box = document.createElement("div");
    box.className = "s-help-empty";
    const img = document.createElement("img");
    img.src = asset(mascotFile.think);
    img.alt = text("Help.MascotAlt", "Sikka");
    const p = document.createElement("p");
    p.textContent = text("Help.Loading", "Loading help…");
    box.append(img, p);
    body.appendChild(box);
}

function renderFallback() {
    const body = panel.querySelector(".s-help-panel-body");
    body.replaceChildren();
    setTitle(text("Help.NoHelpTitle", "No help for this screen yet"));
    const box = document.createElement("div");
    box.className = "s-help-empty";
    const img = document.createElement("img");
    img.src = asset(mascotFile.think);
    img.alt = text("Help.MascotAlt", "Sikka");
    const p = document.createElement("p");
    p.textContent = text("Help.NoHelpBody",
        "Sikka doesn't have notes for this screen yet. The Help Center has guides for the rest of mERP26.");
    const link = document.createElement("a");
    link.className = "s-help-center-link";
    link.href = helpCenterUrl();
    link.textContent = text("Help.OpenHelpCenter", "Open Help Center");
    box.append(img, p, link);
    body.appendChild(box);
    body.appendChild(trainingCard());
}

function renderScreen(doc: ScreenHelp) {
    const body = panel.querySelector(".s-help-panel-body");
    body.replaceChildren();
    setTitle(doc.title || text("Help.PanelTitle", "Help"));

    if (doc === pinned && searchQuery.trim()) {
        const back = document.createElement("button");
        back.type = "button";
        back.className = "s-help-back";
        back.textContent = text("Help.BackToResults", "Back to results");
        back.addEventListener("click", () => {
            pinned = null;
            render();
        });
        body.appendChild(back);
    }

    if (doc.howtoTitle || (doc.steps && doc.steps.length)) {
        const block = document.createElement("section");
        block.className = "s-help-howto";
        if (doc.howtoTitle) {
            const heading = document.createElement("h3");
            heading.textContent = doc.howtoTitle;
            block.appendChild(heading);
        }
        if (doc.steps?.length) {
            const list = document.createElement("ol");
            doc.steps.forEach(step => {
                const item = document.createElement("li");
                item.textContent = step;
                list.appendChild(item);
            });
            block.appendChild(list);
        }
        body.appendChild(block);
    }

    const media = safeMedia(doc.videoUrl || "");
    if (media) {
        const figure = document.createElement("figure");
        figure.className = "s-help-media";
        if (/\.(gif|png|jpe?g|webp|svg)(\?|$)/i.test(media)) {
            const img = document.createElement("img");
            img.src = media;
            img.alt = doc.videoCaption || doc.title || "";
            figure.appendChild(img);
        }
        else {
            const video = document.createElement("video");
            video.controls = true;
            video.playsInline = true;
            video.preload = "metadata";
            video.src = media;
            figure.appendChild(video);
        }
        if (doc.videoCaption) {
            const cap = document.createElement("figcaption");
            cap.textContent = doc.videoCaption;
            figure.appendChild(cap);
        }
        body.appendChild(figure);
    }

    if (canOfferTour(doc)) {
        const start = document.createElement("button");
        start.type = "button";
        start.className = "s-help-tour-start";
        start.textContent = text("Help.StartTour", "Start tour");
        start.addEventListener("click", () => beginTour(doc));
        body.appendChild(start);
    }

    body.appendChild(trainingCard());

    const more = document.createElement("a");
    more.className = "s-help-center-link s-help-center-link-quiet";
    if (doc.helpCenterKey) {
        more.href = helpCenterUrl() + "?topic=" + encodeURIComponent(doc.helpCenterKey);
        more.textContent = text("Help.OpenHelpCenterArticle", "Open the full article");
    }
    else {
        more.href = helpCenterUrl();
        more.textContent = text("Help.OpenHelpCenter", "Open Help Center");
    }
    body.appendChild(more);
}

function trainingCard(): HTMLElement {
    const card = document.createElement("div");
    card.className = "s-help-training";
    const copy = document.createElement("div");
    const title = document.createElement("div");
    title.className = "s-help-training-title";
    title.textContent = text("Help.TrainingMode", "Training mode");
    const hint = document.createElement("div");
    hint.className = "s-help-training-hint";
    hint.textContent = text("Help.TrainingModeHint", "While this is on, Sikka starts the guided tour on every visit");
    copy.append(title, hint);

    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = "s-help-switch";
    toggle.setAttribute("role", "switch");
    toggle.setAttribute("aria-checked", state.trainingMode ? "true" : "false");
    toggle.setAttribute("aria-label", text("Help.TrainingMode", "Training mode"));
    toggle.addEventListener("click", () => {
        const on = !state.trainingMode;
        state = { ...state, trainingMode: on };
        toggle.setAttribute("aria-checked", on ? "true" : "false");
        persist();
        if (!on) {
            stopAutoHelp();
            return;
        }
        maybeGreet();
        if (canOfferTour(screen))
            void startTour(screen, true, true);
    });

    card.append(copy, toggle);
    return card;
}

function render() {
    ensureChrome();
    if (pinned) {
        renderScreen(pinned);
        return;
    }
    const query = searchQuery.trim();
    if (query) {
        const generation = ++searchGeneration;
        if (indexCache) {
            paintSearch(query, indexCache);
            return;
        }
        renderLoading();
        void ensureIndex().then(index => {
            if (generation !== searchGeneration || pinned || searchQuery.trim() !== query)
                return;
            paintSearch(query, index);
        });
        return;
    }
    if (!screenReady)
        renderLoading();
    else if (!screen?.title)
        renderFallback();
    else
        renderScreen(screen);
}

async function openPanel(focusSearchBox = false) {
    ensureChrome();
    render();
    backdrop.classList.add("s-open");
    panel.classList.add("s-open");
    panel.setAttribute("aria-hidden", "false");
    openButton?.setAttribute("aria-expanded", "true");
    if (focusSearchBox && searchInput) {
        searchInput.focus();
        searchInput.select();
        return;
    }
    (panel.querySelector(".s-help-panel-close") as HTMLButtonElement)?.focus();
}

function closePanel() {
    if (!panel)
        return;
    backdrop.classList.remove("s-open");
    panel.classList.remove("s-open");
    panel.setAttribute("aria-hidden", "true");
    openButton?.setAttribute("aria-expanded", "false");
}

function paintSearch(query: string, index: HelpIndex) {
    const body = panel.querySelector(".s-help-panel-body");
    body.replaceChildren();
    setTitle(text("Help.PanelTitle", "Help"));
    const result = searchHelp(query, index.articles.map(toArticleDoc), index.screens);
    if (!result.articles.length && !result.screens.length) {
        paintNoResults(body);
        return;
    }
    if (result.articles.length)
        body.appendChild(articleGroup(result.articles));
    if (result.screens.length)
        body.appendChild(screenGroup(result.screens));
}

function paintNoResults(body: Element) {
    const box = document.createElement("div");
    box.className = "s-help-empty s-help-no-results";
    const img = document.createElement("img");
    img.src = asset(mascotFile.think);
    img.alt = text("Help.MascotAlt", "Sikka");
    const message = document.createElement("p");
    message.textContent = text("Help.NoResults", "Sikka couldn't find anything for that.");
    box.append(img, message);
    body.appendChild(box);
}

function articleGroup(hits: ReturnType<typeof searchHelp>["articles"]): HTMLElement {
    const group = resultGroup(text("Help.HelpArticles", "Help articles"));
    for (const hit of hits) {
        const doc = indexCache?.articles?.find(item => item.id && item.id === hit.article.id);
        const card = document.createElement("article");
        card.className = "s-help-result";
        const title = document.createElement("div");
        title.className = "s-help-result-title";
        appendHighlighted(title, hit.article.title || "", hit.titleRanges);
        card.appendChild(title);
        if (hit.snippet) {
            const snippet = document.createElement("div");
            snippet.className = "s-help-result-snippet";
            appendHighlighted(snippet, hit.snippet, hit.snippetRanges);
            card.appendChild(snippet);
        }
        const actions: { label: string; onClick: () => void }[] = [];
        if (doc) {
            actions.push({
                label: text("Help.OpenArticle", "Open article"),
                onClick: () => openArticle(doc)
            });
        }
        const url = doc ? permittedUrl(doc) : "";
        if (url && doc && !isCurrentDoc(doc)) {
            actions.push({
                label: text("Help.OpenScreen", "Open screen"),
                onClick: () => goToScreen(url, false)
            });
        }
        if (doc && canOfferTour(doc)) {
            actions.push({
                label: text("Help.StartTour", "Start tour"),
                onClick: () => beginTour(doc)
            });
        }
        card.appendChild(actionRow(actions));
        group.appendChild(card);
    }
    return group;
}

function screenGroup(hits: ReturnType<typeof searchHelp>["screens"]): HTMLElement {
    const group = resultGroup(text("Help.Screens", "Screens"));
    for (const hit of hits) {
        const card = document.createElement("article");
        card.className = "s-help-result";
        const title = document.createElement("div");
        title.className = "s-help-result-title";
        appendHighlighted(title, hit.screen.title || hit.screen.fullPath || "", hit.titleRanges);
        card.appendChild(title);
        if (hit.screen.fullPath) {
            const path = document.createElement("div");
            path.className = "s-help-result-snippet";
            appendHighlighted(path, hit.screen.fullPath, hit.pathRanges);
            card.appendChild(path);
        }
        const doc = articleForUrl(hit.screen.url);
        const actions: { label: string; onClick: () => void }[] = [{
            label: text("Help.OpenScreen", "Open screen"),
            onClick: () => goToScreen(hit.screen.url, false)
        }];
        if (doc)
            actions.unshift({
                label: text("Help.OpenArticle", "Open article"),
                onClick: () => openArticle(doc)
            });
        if (doc && canOfferTour(doc)) {
            actions.push({
                label: text("Help.StartTour", "Start tour"),
                onClick: () => beginTour(doc)
            });
        }
        card.appendChild(actionRow(actions));
        group.appendChild(card);
    }
    return group;
}

function resultGroup(label: string): HTMLElement {
    const group = document.createElement("section");
    group.className = "s-help-results-group";
    const heading = document.createElement("h3");
    heading.textContent = label;
    group.appendChild(heading);
    return group;
}

function actionRow(actions: { label: string; onClick: () => void }[]): HTMLElement {
    const row = document.createElement("div");
    row.className = "s-help-result-actions";
    for (const action of actions) {
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = action.label;
        button.addEventListener("click", action.onClick);
        row.appendChild(button);
    }
    return row;
}

function appendHighlighted(parent: HTMLElement, value: string, ranges: TextRange[]) {
    const sorted = [...(ranges || [])].sort((a, b) => a.start - b.start);
    let cursor = 0;
    for (const range of sorted) {
        const start = Math.max(cursor, Math.min(range.start, value.length));
        const end = Math.max(start, Math.min(range.end, value.length));
        if (start > cursor)
            parent.appendChild(document.createTextNode(value.slice(cursor, start)));
        if (end > start) {
            const mark = document.createElement("mark");
            mark.textContent = value.slice(start, end);
            parent.appendChild(mark);
        }
        cursor = end;
    }
    if (cursor < value.length)
        parent.appendChild(document.createTextNode(value.slice(cursor)));
}

function openArticle(doc: ScreenHelp) {
    if (doc?.helpCenterKey && !doc.routes?.length) {
        location.assign(helpCenterUrl() + "?topic=" + encodeURIComponent(doc.helpCenterKey));
        return;
    }
    pinned = doc;
    render();
}

function toArticleDoc(doc: ScreenHelp) {
    return {
        id: doc.id || "",
        title: doc.title || "",
        routes: doc.routes || [],
        query: doc.query,
        howtoTitle: doc.howtoTitle,
        steps: doc.steps || [],
        tourTexts: (doc.tour?.steps || []).map(step => step?.text || "").filter(step => !!step),
        searchText: doc.searchText || [],
        hasTour: !!doc.tour?.steps?.length,
        keys: doc.keys || [],
        helpCenterKey: doc.helpCenterKey
    };
}

function ensureIndex(): Promise<HelpIndex> {
    if (indexCache)
        return Promise.resolve(indexCache);
    if (!indexPromise) {
        indexPromise = fetchIndex().then(data => {
            indexCache = data;
            return data;
        }).catch(error => {
            console.warn("Help search could not be loaded.", error?.message || error);
            indexPromise = null;
            return { articles: [], screens: [] };
        });
    }
    return indexPromise;
}

async function fetchIndex(): Promise<HelpIndex> {
    const empty = { articles: [], screens: [] };
    const response = await fetch(appRoot() + "Help/Index", {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        redirect: "manual"
    });
    if (response.type === "opaqueredirect")
        return empty;
    if (!response.ok)
        throw new Error("Help index failed (" + response.status + ")");
    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("json"))
        return empty;
    const body = await response.json();
    const articles = Array.isArray(body?.articles) ? body.articles as ScreenHelp[] : [];
    const screens = Array.isArray(body?.screens)
        ? (body.screens as HelpScreenDoc[]).filter(item => item?.url && item.url.trim())
        : [];
    return { articles, screens };
}

function isCurrentDoc(doc: ScreenHelp): boolean {
    if (!doc?.routes?.length)
        return true;
    return screenUrlMatches(pagePath() + location.search, doc.routes, doc.query, appRoot());
}

function permittedUrl(doc: ScreenHelp): string {
    if (!doc?.routes?.length || !indexCache)
        return "";
    for (const item of indexCache.screens) {
        if (item?.url && screenUrlMatches(item.url, doc.routes, doc.query, appRoot()))
            return item.url;
    }
    return "";
}

function articleForUrl(url: string): ScreenHelp {
    let best: ScreenHelp = null;
    let bestScore = 0;
    for (const doc of indexCache?.articles || []) {
        if (!screenUrlMatches(url, doc.routes || [], doc.query, appRoot()))
            continue;
        const score = 1 + (doc.query && Object.keys(doc.query).length ? 5 : 0);
        if (score > bestScore) {
            best = doc;
            bestScore = score;
        }
    }
    return best;
}

function genericTourId(): string {
    const raw = ("grid-" + pagePath() + location.search).toLowerCase();
    const id = raw.replace(/[^a-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "");
    return id.slice(0, 80);
}

/** Builds the three-step grid tour for a screen without its own steps.
 *  Training mode decides whether it starts on its own; Start tour can still run it.
 */
function genericTourSteps(): TourStep[] {
    const steps: TourStep[] = [];
    const newButton = Array.from(document.querySelectorAll<HTMLElement>(".s-Toolbar .tool-button"))
        .find(button => /^new\b/i.test(button.getAttribute("title") || button.textContent || ""));
    if (newButton)
        steps.push({
            element: newButton,
            text: text("Help.GenericTourNew", "Tap here to create a new record on this screen.")
        });
    const searchBox = document.querySelector<HTMLElement>(".s-QuickSearchInput");
    if (searchBox)
        steps.push({
            element: searchBox,
            text: text("Help.GenericTourSearch", "Type here to filter the list as you go.")
        });
    const grid = document.querySelector<HTMLElement>(".s-DataGrid");
    if (grid)
        steps.push({
            element: grid,
            text: text("Help.GenericTourGrid", "The grid lists every record; click a row to open it.")
        });
    return steps;
}

function resolveTour(doc: ScreenHelp): ResolvedTour | null {
    const curated = doc?.tour?.steps?.filter(step => step?.element && step?.text);
    if (curated?.length)
        return { id: doc.tour.id || genericTourId(), steps: curated };
    // Generic tours belong to the current screen's own doc, not to route-less articles.
    if (!doc?.title || !doc.routes?.length || !isCurrentDoc(doc))
        return null;
    const steps = genericTourSteps();
    return steps.length ? { id: genericTourId(), steps } : null;
}

function canOfferTour(doc: ScreenHelp): boolean {
    if (!doc?.title)
        return false;
    if (doc.tour?.steps?.length)
        return isCurrentDoc(doc) || !!permittedUrl(doc);
    return !!doc.routes?.length && isCurrentDoc(doc) && genericTourSteps().length > 0;
}

function beginTour(doc: ScreenHelp) {
    if (isCurrentDoc(doc)) {
        void startTour(doc, true);
        return;
    }
    const url = permittedUrl(doc);
    if (url)
        goToScreen(url, true);
}

function goToScreen(url: string, tour: boolean) {
    const target = withTourFlag(resolveUrl(url), tour);
    if (canonicalScreenUrl(target, appRoot()) === canonicalScreenUrl(pagePath() + location.search, appRoot())) {
        if (tour) {
            const doc = articleForUrl(url) || screen;
            if (doc)
                void startTour(doc, true);
        }
        else
            closePanel();
        return;
    }
    location.assign(target);
}

function resolveUrl(url: string): string {
    if (!url)
        return url;
    if (url.startsWith("~/"))
        return appRoot() + url.slice(2);
    if (url.startsWith("~"))
        return appRoot() + url.slice(1);
    return url;
}

function withTourFlag(url: string, tour: boolean): string {
    if (!tour || /[?&]helpTour=/i.test(url))
        return url;
    const hashAt = url.indexOf("#");
    const hash = hashAt >= 0 ? url.slice(hashAt) : "";
    const bare = hashAt >= 0 ? url.slice(0, hashAt) : url;
    return bare + (bare.includes("?") ? "&" : "?") + "helpTour=1" + hash;
}

function focusSearch() {
    ensureChrome();
    if (!panel.classList.contains("s-open"))
        void openPanel(true);
    else if (searchInput) {
        searchInput.focus();
        searchInput.select();
    }
}

function isTypingTarget(target: EventTarget): boolean {
    const el = target as HTMLElement;
    if (!el?.tagName)
        return false;
    if (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT")
        return true;
    return !!el.isContentEditable;
}

async function fetchScreen(): Promise<ScreenHelp> {
    const params = new URLSearchParams();
    params.set("path", pagePath());
    const search = location.search.startsWith("?") ? location.search.slice(1) : location.search;
    if (search)
        params.set("query", search);
    const explicit = document.querySelector("[data-help-key]")?.getAttribute("data-help-key")
        || new URLSearchParams(location.search).get("help");
    if (explicit)
        params.set("key", explicit);

    const response = await fetch(appRoot() + "Help/Screen?" + params.toString(), {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        redirect: "manual"
    });
    if (response.type === "opaqueredirect" || !response.ok)
        return null;
    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("json"))
        return null;
    const body = await response.json();
    if (!body || !body.title)
        return null;
    return body as ScreenHelp;
}

function loadRemoteState(): Promise<unknown> {
    return new Promise(resolve => {
        let settled = false;
        const finish = (value: unknown) => {
            if (settled)
                return;
            settled = true;
            resolve(value);
        };
        let reported = false;
        const fail = (detail: unknown) => {
            if (!reported) {
                reported = true;
                console.warn("Help preferences could not be loaded.", detail ?? "");
            }
            finish(null);
        };
        try {
            // onError suppresses Serenity's dialog. The returned promise still rejects,
            // so it has to be caught or the browser reports an unhandled rejection.
            void Promise.resolve(serviceRequest("Common/HelpPreference/Load", {}, (response: any) => {
                finish(response ?? {});
            }, {
                blockUI: false,
                onError: (response: any) => {
                    fail(response?.Error?.Message || response?.Error?.Code || "request failed");
                    return true;
                }
            })).catch(error => fail(error?.message || error));
        }
        catch (error) {
            fail(error);
        }
    });
}

function persist() {
    preferenceRevision++;
    writeLocal(state);
    try {
        void Promise.resolve(serviceRequest("Common/HelpPreference/Save", {
            TrainingMode: state.trainingMode,
            CompletedTours: state.completedTours
        }, () => { }, {
            blockUI: false,
            onError: (response: any) => {
                console.warn("Help preferences could not be saved.",
                    response?.Error?.Message || response?.Error?.Code || "request failed");
                return true;
            }
        })).catch(error => console.warn("Help preferences could not be saved.", error?.message || error));
    }
    catch (error) {
        console.warn("Help preferences could not be saved.", error);
    }
}

function rememberTour(id: string) {
    if (!id || state.completedTours.some(item => item.toLowerCase() === id.toLowerCase()))
        return;
    state = { ...state, completedTours: state.completedTours.concat(id) };
    persist();
}

/** "@dialog .selector" means the step lives in the edit dialog. The runner opens it.
 *  Automatic tours never get here while training mode is off, so a menu choice
 *  or an open dialog does not launch Sikka or press New.
 */
function prepareTourStep(step: TourStep): TourStep {
    const element = step.element;
    if (typeof element === "string" && element.startsWith("@dialog "))
        return { ...step, element: element.slice("@dialog ".length).trim(), openDialog: true };
    return step;
}

function pageNewButton(): HTMLElement {
    const selectors = ["[data-tour='grid-new']", "[data-tour='txn-new']", "[data-tour='jv-new']", "[data-tour='bank-new']"];
    for (const selector of selectors) {
        const found = document.querySelector(selector) as HTMLElement;
        if (found && !found.closest(".modal, .ui-dialog"))
            return found;
    }
    return null;
}

function closeTopDialog() {
    const close = document.querySelector(
        ".modal.show .btn-close, .modal.show [data-bs-dismiss='modal'], .ui-dialog-titlebar-close"
    ) as HTMLElement;
    close?.click();
}

async function startTour(doc: ScreenHelp, interactive = true, automatic = false) {
    if (automatic && !shouldStartTour("auto", state))
        return;
    const resolved = resolveTour(doc);
    const steps = (resolved?.steps || []).map(prepareTourStep).filter(step => step?.element && step?.text);
    if (!resolved || !steps.length)
        return;

    const createDriver = await loadDriver();
    if (automatic && !shouldStartTour("auto", state))
        return;
    if (!createDriver) {
        if (interactive)
            showTourUnavailable();
        return;
    }

    const contentCount = steps.length;
    const driveSteps: DriveStep[] = steps.map((step, index) => ({
        element: step.element,
        popover: {
            side: step.side || "bottom",
            align: "start",
            title: text("Help.Greeting", "Hi, I'm Sikka"),
            description: format(text("Help.TourStepLead", "Step {0} of {1}: {2}"),
                String(index + 1), String(contentCount), step.text)
        }
    }));

    driveSteps.push({
        popover: {
            align: "center",
            title: text("Help.TourFinishedTitle", "Nice work"),
            description: text("Help.TourFinishedBody",
                "Hi, I'm Sikka. That's the tour. You can replay it any time from the help panel.")
        }
    });

    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const options: Config = {
        animate: !reduceMotion,
        overlayColor: "#1c2430",
        overlayOpacity: 0.45,
        stagePadding: 8,
        stageRadius: 10,
        popoverOffset: 16,
        allowClose: true,
        smoothScroll: !reduceMotion,
        showProgress: false,
        showButtons: ["next", "previous"],
        popoverClass: "s-help-tour-pop",
        nextBtnText: text("Help.Next", "Next"),
        prevBtnText: text("Help.Back", "Back"),
        doneBtnText: text("Help.Done", "Done"),
        steps: driveSteps,
        onNextClick: (_element, _step, opts) => {
            if (automatic && !shouldStartTour("auto", state)) {
                opts.driver.destroy();
                return;
            }
            const index = opts.state.activeIndex ?? 0;
            const next = steps[index + 1];
            const go = () => opts.driver.moveNext();
            if (next?.openDialog && typeof next.element === "string" && !document.querySelector(next.element)) {
                const selector = next.element;
                stampTourAnchors();
                pageNewButton()?.click();
                void waitFor(selector).then(() => {
                    stampTourAnchors();
                    go();
                });
                return;
            }
            go();
        },
        onPrevClick: (_element, _step, opts) => {
            const index = opts.state.activeIndex ?? 0;
            const current = steps[index];
            const prev = steps[index - 1];
            if (current?.openDialog && !prev?.openDialog)
                closeTopDialog();
            opts.driver.movePrevious();
        },
        onPopoverRender: (popover, opts) => {
            const index = opts.state.activeIndex ?? 0;
            const finale = index >= contentCount;
            popover.wrapper.classList.toggle("s-help-tour-finale", finale);
            popover.progress.style.display = "none";
            popover.closeButton.style.display = "none";
            if (finale) {
                popover.title.textContent = text("Help.TourFinishedTitle", "Nice work");
                popover.description.textContent = text("Help.TourFinishedBody",
                    "Hi, I'm Sikka. That's the tour. You can replay it any time from the help panel.");
            }
            else {
                popover.title.textContent = text("Help.Greeting", "Hi, I'm Sikka");
                popover.description.textContent = format(
                    text("Help.TourStepLead", "Step {0} of {1}: {2}"),
                    String(index + 1), String(contentCount), steps[index]?.text || "");
            }

            let kicker = popover.wrapper.querySelector(".s-help-tour-kicker");
            if (!kicker) {
                kicker = document.createElement("div");
                kicker.className = "s-help-tour-kicker";
                popover.wrapper.insertBefore(kicker, popover.title);
            }
            kicker.textContent = finale
                ? text("Help.TourFinishedKicker", "Guided tour")
                : format(text("Help.TourKicker", "Guided tour · Step {0} of {1}"),
                    String(index + 1), String(contentCount));

            let mascot = popover.wrapper.querySelector(".s-help-tour-mascot") as HTMLImageElement;
            if (!mascot) {
                mascot = document.createElement("img");
                mascot.className = "s-help-tour-mascot";
                mascot.alt = text("Help.MascotAlt", "Sikka");
                popover.wrapper.appendChild(mascot);
            }
            mascot.src = asset(finale ? mascotFile.celebrate : mascotFile.point);

            let dots = popover.footer.querySelector(".s-help-dots");
            if (!dots) {
                dots = document.createElement("div");
                dots.className = "s-help-dots";
                popover.footer.prepend(dots);
            }
            dots.replaceChildren();
            for (let i = 0; i < contentCount; i++) {
                const dot = document.createElement("i");
                if (i <= index || finale)
                    dot.className = "s-on";
                dots.appendChild(dot);
            }

            let skip = popover.footer.querySelector(".s-help-skip") as HTMLButtonElement;
            if (!skip) {
                skip = document.createElement("button");
                skip.type = "button";
                skip.className = "s-help-skip";
                popover.footer.insertBefore(skip, popover.footerButtons);
            }
            skip.textContent = text("Help.SkipTour", "Skip tour");
            skip.onclick = () => opts.driver.destroy();

            const onFirst = opts.driver.isFirstStep()
                || popover.previousButton.classList.contains("driver-popover-btn-disabled");
            popover.previousButton.style.display = onFirst ? "none" : "inline-block";
            popover.nextButton.classList.toggle("s-help-done", opts.driver.isLastStep());
        },
        onDestroyed: () => {
            const remember = !suppressTourRemember;
            autoTourActive = false;
            tour = null;
            if (remember)
                rememberTour(resolved.id);
        }
    };
    if (automatic && !shouldStartTour("auto", state))
        return;
    tour?.destroy();
    closePanel();
    stampTourAnchors();
    tour = createDriver(options);

    autoTourActive = automatic;
    offered = true;
    tour.drive();
}

function waitFor(selector: string): Promise<boolean> {
    const started = Date.now();
    return new Promise(resolve => {
        const tick = () => {
            stampTourAnchors();
            if (document.querySelector(selector)) {
                resolve(true);
                return;
            }
            if (Date.now() - started > 8000) {
                resolve(false);
                return;
            }
            window.setTimeout(tick, 200);
        };
        tick();
    });
}

async function maybeAutoTour(token: number) {
    if (!canAutoTour(token))
        return;
    let resolved = resolveTour(screen);
    if (!resolved && await waitFor(".s-DataGrid, .s-Toolbar"))
        resolved = resolveTour(screen);
    if (!canAutoTour(token) || !resolved)
        return;
    const first = resolved.steps[0]?.element;
    if (!first)
        return;
    if (typeof first === "string" && !await waitFor(first))
        return;
    if (!canAutoTour(token))
        return;
    await startTour(screen, false, true);
}

function canAutoTour(token: number): boolean {
    return token === screenToken
        && !offered
        && shouldStartTour("auto", state)
        && screenReady
        && !!screen?.title;
}

function whenPreferencesReady(): Promise<void> {
    if (!preferencesReady)
        preferencesReady = settlePreferences();
    return preferencesReady;
}

async function settlePreferences() {
    const revision = preferenceRevision;
    const remote = await loadRemoteState();
    if (revision !== preferenceRevision)
        return;
    state = applyLoadedPreference(state, remote);
    writeLocal(state);
    if (!shouldStartTour("auto", state))
        stopAutoHelp();
    if (panel?.classList.contains("s-open"))
        render();
}

function installNavigationWatch(onChange: () => void) {
    const notify = () => {
        if (locationToken() === activeLocation)
            return;
        onChange();
    };
    window.addEventListener("popstate", notify);
    window.addEventListener("hashchange", notify);
    const push = history.pushState.bind(history);
    const replace = history.replaceState.bind(history);
    history.pushState = (data: any, unused: string, url?: string | URL | null) => {
        push(data, unused, url);
        notify();
    };
    history.replaceState = (data: any, unused: string, url?: string | URL | null) => {
        replace(data, unused, url);
        notify();
    };
}

async function refreshScreen() {
    const token = ++screenToken;
    const key = locationToken();
    if (key !== activeLocation) {
        activeLocation = key;
        endRunningTour();
        offered = false;
    }
    screenReady = false;
    let next: ScreenHelp = null;
    try {
        next = await fetchScreen();
    }
    catch {
        next = null;
    }
    if (token !== screenToken)
        return;
    screen = next;
    screenReady = true;
    if (panel?.classList.contains("s-open"))
        render();

    await whenPreferencesReady();
    if (token !== screenToken)
        return;

    // ?help= is an explicit bookmark, same as pressing the ? button.
    const deepLink = new URLSearchParams(location.search).get("help");
    if (deepLink && !panel?.classList.contains("s-open"))
        void openPanel();

    void ensureIndex();

    // ?helpTour=1 is how Help Center and search Start tour land on this screen.
    if (consumeTourLink()) {
        offered = true;
        if (screen)
            await startLinkedTour(token);
        return;
    }

    if (!shouldStartTour("auto", state))
        return;
    await maybeAutoTour(token);
}

async function boot() {
    // no help chrome (floating mascot button, panel, tours) on the login page
    if (document.body?.id === "s-LoginPage")
        return;
    state = readLocal();
    activeLocation = locationToken();
    ensureChrome();
    installNavigationWatch(() => { void refreshScreen(); });
    document.addEventListener("keydown", event => {
        if (event.key === "Escape" && panel?.classList.contains("s-open") && !event.defaultPrevented) {
            closePanel();
            return;
        }
        if (event.repeat || event.isComposing)
            return;
        const slash = (event.ctrlKey || event.metaKey) && !event.altKey
            && (event.key === "/" || event.code === "Slash");
        const question = event.key === "?" && !event.ctrlKey && !event.metaKey && !event.altKey
            && !isTypingTarget(event.target);
        if (!slash && !question)
            return;
        event.preventDefault();
        focusSearch();
    });
    window.addEventListener("pageshow", event => {
        if (!event.persisted)
            return;
        state = readLocal();
        if (!shouldStartTour("auto", state))
            stopAutoHelp();
        else if (!tour && !offered)
            void maybeAutoTour(screenToken);
        if (panel?.classList.contains("s-open"))
            render();
    });

    await whenPreferencesReady();
    if (shouldStartTour("auto", state))
        maybeGreet();
    await refreshScreen();
}

function consumeTourLink(): boolean {
    const params = new URLSearchParams(location.search);
    if (params.get("helpTour") !== "1")
        return false;
    params.delete("helpTour");
    const next = location.pathname + (params.toString() ? "?" + params.toString() : "") + location.hash;
    history.replaceState(history.state, "", next);
    return true;
}

async function startLinkedTour(token: number) {
    if (!screen || token !== screenToken)
        return;
    let resolved = resolveTour(screen);
    if (!resolved && await waitFor(".s-DataGrid, .s-Toolbar"))
        resolved = resolveTour(screen);
    if (token !== screenToken || !resolved)
        return;
    const first = resolved.steps[0]?.element;
    if (typeof first === "string" && first)
        await waitFor(first);
    if (token !== screenToken)
        return;
    await startTour(screen, true, false);
}

if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", () => { void boot(); });
else
    void boot();

window.sHelpPanel = {
    open: () => { void openPanel(); },
    close: closePanel,
    startTour: () => { if (screen) void startTour(screen); }
};
